import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Login from './pages/auth/Login'
import ProtectedRoute from './components/ProtectedRoute'
import MainLayout from './layouts/MainLayout'
import Home from './pages/Home'
import Invoices from './pages/invoices/Invoices'
import CreateInvoice from './pages/invoices/CreateInvoice'
import InvoiceDetails from './pages/invoices/InvoiceDetails'
import CreateReceipt from './pages/receipts/CreateReceipt'
import Customers from './pages/customers/Customers'
import Products from './pages/products/Products'
import Inventory from './pages/inventory/Inventory'
import Transfers from './pages/transfers/Transfers'
import TransferDetails from './pages/transfers/TransferDetails'
import Profile from './pages/profile/Profile'
import EditProfile from './pages/profile/EditProfile'
import ChangePassword from './pages/profile/ChangePassword'
import DailyReport from './pages/reports/DailyReport'
import Notifications from './pages/notifications/Notifications'
import useNotificationStore from './stores/useNotificationStore'
import useAuthStore from './stores/useAuthStore'

/**
 * Main App Component
 * Setup routing with authentication and bottom navigation
 * Includes notification polling for real-time updates (P002: HTTP Polling Protocol)
 */
function App() {
  const { isAuthenticated } = useAuthStore()
  const { startPolling, stopPolling } = useNotificationStore()

  // Start/stop polling based on authentication
  // Note: Only isAuthenticated in dependencies to prevent re-renders
  // startPolling/stopPolling are stable functions from Zustand store
  useEffect(() => {
    if (isAuthenticated) {
      startPolling() // Poll every 2 minutes
      console.log('[App] Notification polling started')
    } else {
      stopPolling()
      console.log('[App] Notification polling stopped')
    }

    // Cleanup on unmount
    return () => {
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected Routes with Main Layout (Bottom Navigation) */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - Home */}
          <Route path="/" element={<Home />} />

          {/* Invoices */}
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/create" element={<CreateInvoice />} />
          <Route path="/invoices/:id" element={<InvoiceDetails />} />
          <Route path="/invoices/edit/:id" element={<CreateInvoice />} />

          {/* Receipts - NEW INDEPENDENT RECEIPT SYSTEM (December 2025) */}
          <Route path="/receipts/create" element={<CreateReceipt />} />

          {/* Customers */}
          <Route path="/customers" element={<Customers />} />

          {/* Products - Old page (kept for backwards compatibility) */}
          <Route path="/products" element={<Products />} />

          {/* Inventory - New comprehensive page */}
          <Route path="/inventory" element={<Inventory />} />

          {/* Transfers - Warehouse Transfer Management */}
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/transfers/:id" element={<TransferDetails />} />

          {/* Reports */}
          <Route path="/reports/daily" element={<DailyReport />} />

          {/* Notifications */}
          <Route path="/notifications" element={<Notifications />} />

          {/* Profile */}
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/edit" element={<EditProfile />} />
          <Route path="/profile/change-password" element={<ChangePassword />} />
        </Route>

        {/* Catch All - Redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
